/**
 * Policies list (OR-1664, design OR-1537) — the book of policies, one row
 * per policy read from its current term, served by `GET /v2/policies`.
 *
 * Layout shape (same shell as Submission Triage):
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Sidebar │ Page header (title + subtitle)  ← non-scrolling     │
 *   │         ├──────────────────────────────────────────────────── │
 *   │         │ Toolbar (filters · customize · search)              │
 *   │         ├──────────────────────────────────────────────────── │
 *   │         │ Table headers ← sticky                               │
 *   │         │ ┌──────────── scroll area ─────────────────────────┐ │
 *   │         │ │ rows                                             │ │
 *   │         │ └──────────────────────────────────────────────────┘ │
 *   │         ├──────────────────────────────────────────────────── │
 *   │         │ Pagination footer ← non-scrolling                    │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Search, filters, sort and paging are all server-side. The layout the
 * user leaves (columns + filters) is remembered on their profile.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { listPolicies } from "../../api";
import type { ListPoliciesParams, PolicyListResponse } from "../../api";
import { CustomizeColumnsDrawer } from "../../components/customize-columns-drawer";
import { ListEmptyState, ListErrorState, ListOfflineState } from "../../components/list-states";
import { OrionSidebar } from "../../components/orion-sidebar";
import { PaginationFooter } from "../../components/pagination-footer";
import { useDebouncedValue } from "../../hooks/use-debounced-value";
import { ActiveFiltersBar } from "./active-filters-bar";
import { EMPTY_FILTERS, type PolicyFilters, countActiveFilters, filtersToParams } from "./filters";
import { FiltersDrawer } from "./filters-drawer";
import { PoliciesTable } from "./policies-table";
import { GROUP_LABELS, GROUP_ORDER, POLICY_COLUMNS, allColumnIds, defaultVisibleIds } from "./policy-columns";
import { PoliciesToolbar } from "./toolbar";
import { usePoliciesLayout } from "./use-policies-layout";

const DEFAULT_PAGE_SIZE = 10;

type QueryState = { status: "loading" } | { status: "ready"; data: PolicyListResponse } | { status: "error"; error: Error };

/** Fetch a page of policies whenever the params change. `enabled` holds
 *  the first request until the saved layout is in state, so the list
 *  doesn't flash an unfiltered page and immediately refetch. `reloadKey`
 *  forces a refetch with the same params (Try again). */
function usePolicies(params: ListPoliciesParams, enabled: boolean, reloadKey: number): QueryState {
    const [state, setState] = useState<QueryState>({ status: "loading" });
    const key = JSON.stringify(params);
    useEffect(() => {
        let cancelled = false;
        setState({ status: "loading" });
        if (!enabled) return;
        listPolicies(params)
            .then((data) => {
                if (!cancelled) setState({ status: "ready", data });
            })
            .catch((error: Error) => {
                if (!cancelled) setState({ status: "error", error });
            });
        return () => {
            cancelled = true;
        };
        // `params` is JSON-encoded into `key` so the effect re-runs only
        // when a value actually changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled, reloadKey]);
    return state;
}

export function PoliciesListPage() {
    const [offset, setOffset] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [search, setSearch] = useState("");
    /** Sort directive in FastAPI format: `field` asc, `-field` desc.
     *  Undefined lets the backend default (`-effective_date`) apply. */
    const [sort, setSort] = useState<string | undefined>(undefined);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [customizeOpen, setCustomizeOpen] = useState(false);
    /** Committed filters — written by the drawer's Show results / Clear
     *  All and by chip removal, never on individual control changes. */
    const [filters, setFilters] = useState<PolicyFilters>(EMPTY_FILTERS);
    const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(() => defaultVisibleIds());
    const [columnOrder, setColumnOrder] = useState<string[]>(() => allColumnIds());
    const [reloadKey, setReloadKey] = useState(0);

    // Restore the layout the user last left the page in. Runs exactly once;
    // after that the user's own edits own the state.
    const saved = usePoliciesLayout();
    const hydrating = useRef(false);
    const [hydrated, setHydrated] = useState(false);
    useEffect(() => {
        if (saved.status !== "ready" || hydrating.current) return;
        hydrating.current = true;
        if (saved.layout) {
            setVisibleColumnIds(saved.layout.visibleColumnIds);
            setColumnOrder(saved.layout.columnOrder);
            setFilters(saved.layout.filters);
        }
        setHydrated(true);
    }, [saved.status, saved.layout]);
    const canEdit = hydrated && saved.canWrite;

    /** Record the resulting layout after any commit. Callers pass only
     *  what they changed; the rest is read from current state. */
    const rememberLayout = useCallback(
        (patch: Partial<{ visibleColumnIds: string[]; columnOrder: string[]; filters: PolicyFilters }> = {}) => {
            saved.remember({ visibleColumnIds, columnOrder, filters, ...patch });
        },
        [saved, visibleColumnIds, columnOrder, filters],
    );

    const commitFilters = useCallback(
        (next: PolicyFilters) => {
            setOffset(0);
            setFilters(next);
            rememberLayout({ filters: next });
        },
        [rememberLayout],
    );
    const clearAllFilters = useCallback(() => commitFilters(EMPTY_FILTERS), [commitFilters]);

    const debouncedSearch = useDebouncedValue(search.trim(), 300);
    const query = usePolicies(
        {
            offset,
            limit: pageSize,
            search: debouncedSearch || undefined,
            sort,
            ...filtersToParams(filters),
        },
        hydrated,
        reloadKey,
    );
    const filterCount = countActiveFilters(filters);

    const total = query.status === "ready" ? query.data.total : 0;
    const rows = query.status === "ready" ? query.data.items : [];

    const isOffline = query.status === "error" && typeof navigator !== "undefined" && !navigator.onLine;
    const bodyState: "error" | "offline" | "no-results" | "data-or-loading" = isOffline
        ? "offline"
        : query.status === "error"
          ? "error"
          : query.status === "ready" && rows.length === 0
            ? "no-results"
            : "data-or-loading";

    return (
        <div className="bg-secondary flex min-h-screen flex-col lg:h-screen lg:flex-row">
            <OrionSidebar />

            <div className="flex flex-1 flex-col overflow-hidden">
                <header className="flex flex-col gap-1 px-4 pb-4 pt-4 md:px-6 md:pb-6 md:pt-6">
                    <h1 className="text-primary text-xl font-semibold">Policies</h1>
                    <p className="text-md text-secondary">Manage policies with visibility into every lifecycle event</p>
                </header>

                <section className="relative flex min-h-0 flex-1 px-0 pb-0 sm:px-6 sm:pb-6">
                    <div className="bg-primary border-secondary flex min-h-0 w-full flex-1 flex-col overflow-hidden border-t sm:rounded-xl sm:border sm:shadow-xs">
                        <PoliciesToolbar
                            search={search}
                            onSearchChange={(value) => {
                                setOffset(0);
                                setSearch(value);
                            }}
                            filterCount={filterCount}
                            onOpenFilters={() => setFiltersOpen(true)}
                            onOpenCustomize={() => setCustomizeOpen(true)}
                            isDisabled={!canEdit}
                        />

                        <ActiveFiltersBar filters={filters} total={total} onChange={commitFilters} onClearAll={clearAllFilters} />

                        {bodyState === "error" && <ListErrorState onRetry={() => setReloadKey((k) => k + 1)} />}
                        {bodyState === "offline" && <ListOfflineState onRefresh={() => window.location.reload()} />}
                        {bodyState === "no-results" && <ListEmptyState />}
                        {bodyState === "data-or-loading" && (
                            <>
                                <PoliciesTable
                                    rows={rows}
                                    loading={query.status === "loading"}
                                    sort={sort}
                                    onSortChange={(next) => {
                                        setOffset(0);
                                        setSort(next);
                                    }}
                                    visibleColumnIds={visibleColumnIds}
                                    columnOrder={columnOrder}
                                />
                                <PaginationFooter
                                    total={total}
                                    offset={offset}
                                    limit={pageSize}
                                    onChange={setOffset}
                                    onLimitChange={setPageSize}
                                    isDisabled={query.status === "loading"}
                                />
                            </>
                        )}
                    </div>
                </section>
            </div>

            <FiltersDrawer isOpen={filtersOpen} onOpenChange={setFiltersOpen} value={filters} onApply={commitFilters} onClearAll={clearAllFilters} />

            <CustomizeColumnsDrawer
                isOpen={customizeOpen}
                onOpenChange={setCustomizeOpen}
                columns={POLICY_COLUMNS}
                groupOrder={GROUP_ORDER}
                groupLabels={GROUP_LABELS}
                value={visibleColumnIds}
                order={columnOrder}
                defaultVisibleIds={defaultVisibleIds()}
                defaultOrder={allColumnIds()}
                autotestPrefix="policies"
                onApply={(next) => {
                    setVisibleColumnIds(next.visibleIds);
                    setColumnOrder(next.order);
                    rememberLayout({ visibleColumnIds: next.visibleIds, columnOrder: next.order });
                }}
            />
        </div>
    );
}
